import { AccredibleLanguageService } from '@accredible-frontend-v2/services/language';
import { AccredibleUserApiService } from '@accredible-frontend-v2/services/user';
import { inject, Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  BulletPoint,
  DashboardSpeakYourSkillsData,
  SpeakYourSkillsData,
  StatementSection,
  StatementSectionType,
} from '../entities/speak-your-skills.model';
import { LerBulletPointsApiService } from './ler-bullet-points-api.service';
import { LerCredentialFacade } from './ler-credential.facade';

const TRANSLATION_PREFIX = 'ler-statement-section.';

const SECTION_TITLE_KEYS: Record<StatementSectionType, string> = {
  resume: `${TRANSLATION_PREFIX}resume_title`,
  interview: `${TRANSLATION_PREFIX}interviews_title`,
  linkedin: `${TRANSLATION_PREFIX}linkedin_title`,
  email: `${TRANSLATION_PREFIX}email_title`,
};

const SECTION_COUNT_KEYS: Partial<Record<StatementSectionType, string>> = {
  linkedin: `${TRANSLATION_PREFIX}linkedin_count`,
  email: `${TRANSLATION_PREFIX}email_count`,
  interview: `${TRANSLATION_PREFIX}interview_count`,
};

const SECTION_ORDER: StatementSectionType[] = ['resume', 'linkedin', 'email', 'interview'];

const PLACEHOLDER_STATEMENTS = [
  {
    text: 'Demonstrated ability to apply technical concepts in real-world scenarios',
    blurred: true,
  },
  {
    text: 'Proven expertise in collaborative problem-solving and effective communication',
    blurred: true,
  },
] as const;

@Injectable({ providedIn: 'root' })
export class SpeakYourSkillsService {
  private readonly _bulletPointsApi = inject(LerBulletPointsApiService);
  private readonly _credentialApi = inject(LerCredentialFacade);
  private readonly _userApi = inject(AccredibleUserApiService);
  private readonly _language = inject(AccredibleLanguageService);

  loadByCredentialUuid(credentialUuid: string): Observable<SpeakYourSkillsData> {
    return forkJoin({
      bulletPoints: this._bulletPointsApi.loadBulletPoints(credentialUuid),
      credential: this._credentialApi.loadCredential(credentialUuid),
    }).pipe(
      map(({ bulletPoints, credential }) => ({
        credentialName: credential.name,
        issuerName: credential.issuerName,
        sections: this._mapBulletPointsToSections(bulletPoints.bullet_points),
      })),
    );
  }

  loadDashboardData(): Observable<DashboardSpeakYourSkillsData> {
    const userId = this._userApi.getUser().id;

    return this._credentialApi.loadAllCredentialOptions(userId).pipe(
      switchMap((credentials) => {
        if (!credentials.length) {
          return of(null);
        }
        return this._bulletPointsApi.loadBulletPoints(credentials[0].uuid).pipe(
          map((bulletPoints) => ({
            sections: this._mapBulletPointsToSections(bulletPoints.bullet_points),
            credentials,
          })),
        );
      }),
      catchError(() => of(null)),
    );
  }

  loadSectionsForCredential(credentialUuid: string): Observable<StatementSection[]> {
    return this._bulletPointsApi
      .loadBulletPoints(credentialUuid)
      .pipe(map((bulletPoints) => this._mapBulletPointsToSections(bulletPoints.bullet_points)));
  }

  private _mapBulletPointsToSections(bulletPoints: BulletPoint[]): StatementSection[] {
    const grouped = new Map<StatementSectionType, BulletPoint[]>();

    for (const bp of bulletPoints) {
      const list = grouped.get(bp.platform) ?? [];
      list.push(bp);
      grouped.set(bp.platform, list);
    }

    return SECTION_ORDER.map((type) => {
      const title = this._language.translate(SECTION_TITLE_KEYS[type]);
      const statements = grouped.has(type)
        ? grouped.get(type).map((bp) => ({ text: bp.text, blurred: false }))
        : [...PLACEHOLDER_STATEMENTS];

      const countKey = SECTION_COUNT_KEYS[type];
      const countLabel =
        countKey && grouped.has(type)
          ? this._language.translate(countKey, { count: statements.length })
          : '';

      return { type, title, countLabel, statements };
    });
  }
}
